import React, { useState } from 'react';
import { Phone, X, Heart, ShieldCheck, Car, ShoppingBag, RefreshCw, PieChart, CheckCircle2, Calendar, AlertCircle, Briefcase, Home, Shield } from 'lucide-react';
import { Button } from './ui/button';
import { submitAppointment } from '../lib/leadSubmit';

const categoryIcons = {
  Health: Heart,
  Life: ShieldCheck,
  Motor: Car,
  'Personal Accident': Shield,
  'Home Insurance': Home,
  'Group Insurance': Briefcase,
  'Workmen Compensation': Briefcase,
  'Fire & Burglary': ShieldCheck,
  Marine: ShieldCheck,
  'Business Associate': Briefcase,
};
const reasonIcons = { 'Purchase a New Policy': ShoppingBag, 'Renew Policy': RefreshCw, 'Review Insurance Portfolio': PieChart };

const ExpertWidget = () => {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [category, setCategory] = useState('Health');
  const [reason, setReason] = useState('Purchase a New Policy');
  const [slot, setSlot] = useState('');
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const slots = ['10:00 AM', '11:30 AM', '01:00 PM', '03:00 PM', '05:00 PM', '06:30 PM'];

  const reset = () => { setOpen(false); setStep(1); setSlot(''); setPhone(''); setError(''); setLoading(false); };

  const handleScheduleAppointment = async () => {
    setError('');
    setLoading(true);
    
    try {
      const result = await submitAppointment({
        category,
        reason,
        slot,
        phone,
      });
      
      if (result.ok) {
        setStep(3);
      } else {
        setError('Appointment submitted! You will receive a confirmation call shortly.');
        setTimeout(() => setStep(3), 2000);
      }
    } catch (err) {
      setError('Error booking appointment. Please try again.');
      console.error('Appointment booking error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-20 right-5 z-40 bg-white shadow-2xl rounded-full pl-2 pr-5 py-2 flex items-center gap-3 border border-slate-100 hover:shadow-emerald-200 hover:-translate-y-0.5 transition-all group"
        >
          <div className="w-11 h-11 rounded-full bg-gradient-to-br from-emerald-400 to-teal-600 flex items-center justify-center border-2 border-white shadow overflow-hidden">
            <img
              src="/insure_logo.jpeg"
              alt="InsureRiskFree Solutions"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="text-left hidden sm:block">
            <div className="text-[11px] text-slate-500">Hello, I am Smart-Insure-Guru</div>
            <div className="text-sm font-semibold text-[#0F2A4B] group-hover:text-[#00B48E]">Book an Expert Call</div>
          </div>
          <div className="sm:hidden text-sm font-semibold text-[#00B48E]">Expert</div>
        </button>
      )}

      {open && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:justify-end p-0 sm:p-5 bg-black/40 backdrop-blur-sm" onClick={reset}>
          <div className="bg-white w-full sm:w-[420px] rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="bg-gradient-to-br from-emerald-500 to-teal-600 p-5 text-white flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-full bg-white/20 backdrop-blur border-2 border-white/40 overflow-hidden">
                  <img
                      src="/insure_logo.jpeg"
                    alt="InsureRiskFree Solutions"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <div className="text-xs opacity-90">Your Expert Advisor</div>
                  <div className="font-semibold">Smart-Insure-Guru • Book a Call</div>
                </div>
              </div>
              <button onClick={reset} className="w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center"><X className="w-4 h-4" /></button>
            </div>

            <div className="p-5 max-h-[70vh] overflow-y-auto">
              {step === 1 && (
                <>
                  <h3 className="font-semibold text-[#0F2A4B] mb-1">Insurance Category</h3>
                  <p className="text-xs text-slate-500 mb-3">Choose the insurance category you’d like help with</p>
                  <div className="grid grid-cols-2 gap-2 mb-5">
                    {['Health', 'Life', 'Motor', 'Personal Accident', 'Home Insurance', 'Group Insurance', 'Workmen Compensation', 'Fire & Burglary', 'Marine', 'Business Associate'].map((c) => {
                      const Icon = categoryIcons[c];
                      const active = category === c;
                      return (
                        <button key={c} onClick={() => setCategory(c)} className={`p-3 rounded-xl border-2 flex flex-col items-center gap-1 transition-all ${active ? 'border-[#00B48E] bg-emerald-50' : 'border-slate-200 hover:border-slate-300'}`}>
                          <Icon className={`w-5 h-5 ${active ? 'text-[#00B48E]' : 'text-slate-500'}`} />
                          <span className={`text-xs font-medium ${active ? 'text-[#00B48E]' : 'text-slate-700'}`}>{c}</span>
                        </button>
                      );
                    })}
                  </div>

                  <h3 className="font-semibold text-[#0F2A4B] mb-3">Reason for Consultation</h3>
                  <div className="space-y-2 mb-5">
                    {Object.keys(reasonIcons).map((r) => {
                      const Icon = reasonIcons[r];
                      const active = reason === r;
                      return (
                        <button key={r} onClick={() => setReason(r)} className={`w-full p-3 rounded-xl border-2 flex items-center gap-3 transition-all ${active ? 'border-[#00B48E] bg-emerald-50' : 'border-slate-200 hover:border-slate-300'}`}>
                          <Icon className={`w-5 h-5 ${active ? 'text-[#00B48E]' : 'text-slate-500'}`} />
                          <span className={`text-sm font-medium ${active ? 'text-[#00B48E]' : 'text-slate-700'}`}>{r}</span>
                        </button>
                      );
                    })}
                  </div>
                  <Button onClick={() => setStep(2)} className="w-full bg-[#00B48E] hover:bg-[#00a080] text-white rounded-xl py-6 font-semibold">Continue</Button>
                </>
              )}

              {step === 2 && (
                <>
                  <h3 className="font-semibold text-[#0F2A4B] mb-3">Select a Time Slot</h3>
                  <div className="grid grid-cols-3 gap-2 mb-5">
                    {slots.map((s) => (
                      <button key={s} onClick={() => setSlot(s)} className={`p-3 rounded-xl border-2 text-xs font-medium transition-all ${slot === s ? 'border-[#00B48E] bg-emerald-50 text-[#00B48E]' : 'border-slate-200 text-slate-700 hover:border-slate-300'}`}>{s}</button>
                    ))}
                  </div>
                  <h3 className="font-semibold text-[#0F2A4B] mb-3">Enter Your Mobile Number</h3>
                  <div className="flex items-center border-2 border-slate-200 rounded-xl overflow-hidden mb-5 focus-within:border-[#00B48E]">
                    <span className="px-3 py-3 bg-slate-50 text-slate-700 text-sm font-medium border-r">+91</span>
                    <input value={phone} onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))} className="flex-1 px-3 py-3 text-sm outline-none" placeholder="10-digit mobile number" />
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setStep(1)} className="flex-1 rounded-xl py-6">Back</Button>
                    <Button disabled={!slot || phone.length !== 10 || loading} onClick={handleScheduleAppointment} className="flex-1 bg-[#00B48E] hover:bg-[#00a080] text-white rounded-xl py-6 font-semibold disabled:opacity-50">
                      {loading ? 'Scheduling...' : 'Schedule'}
                    </Button>
                  </div>
                  {error && (
                    <div className="mt-3 flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
                      <AlertCircle className="w-4 h-4" />
                      {error}
                    </div>
                  )}
                </>
              )}

              {step === 3 && (
                <div className="text-center py-4">
                  <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle2 className="w-9 h-9 text-[#00B48E]" />
                  </div>
                  <h3 className="text-xl font-bold text-[#0F2A4B]">Congratulations!</h3>
                  <p className="text-slate-600 text-sm mt-1">Your call has been scheduled</p>
                  <div className="mt-5 bg-emerald-50 border border-emerald-100 rounded-2xl p-4 text-left">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-full bg-white overflow-hidden ring-2 ring-emerald-100">
                        <img
                          src="/insure_logo.jpeg"
                          alt="InsureRiskFree Solutions"
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div>
                        <div className="font-semibold text-[#0F2A4B] text-sm">Sharath/Bhavya • Expert Advisor</div>
                        <div className="text-xs text-slate-600">Trusted insurance advisory</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-slate-700">
                      <Calendar className="w-4 h-4 text-[#00B48E]" /> Today • {slot}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-slate-700 mt-1">
                      <Phone className="w-4 h-4 text-[#00B48E]" /> +91 {phone}
                    </div>
                  </div>
                  <Button onClick={reset} className="w-full mt-5 bg-[#00B48E] hover:bg-[#00a080] text-white rounded-xl py-6 font-semibold">Done</Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ExpertWidget;
